import {
  MicIcon,
  MicOffIcon,
  VideoOnIcon,
  VideoOffIcon,
  FlipCameraIcon,
  PipIcon,
  GridIcon,
  StageIcon,
  PhoneOffIcon,
  RecordIcon,
  StopRecordIcon,
} from './Icons.jsx';

/**
 * @typedef {object} CallControlsProps
 * @property {boolean} muted
 * @property {boolean} videoOn
 * @property {'operator'|'client'} role
 * @property {(muted: boolean) => void} onToggleMic
 * @property {(on: boolean) => void} onToggleVideo
 * @property {() => void} [onSwitchCamera]
 * @property {boolean} [hasMultipleCameras=false]
 * @property {() => void} [onTogglePip]
 * @property {boolean} [isPipActive=false]
 * @property {boolean} [isPipSupported=false]
 * @property {'stage'|'grid'} [layoutMode='stage']
 * @property {() => void} [onToggleLayout]
 * @property {boolean} [recording=false]
 * @property {() => void} [onToggleRecording]
 * @property {() => void} onLeave
 * @property {string} [className='']
 */
export default function CallControls({
  muted,
  videoOn,
  role,
  onToggleMic,
  onToggleVideo,
  onSwitchCamera,
  hasMultipleCameras = false,
  onTogglePip,
  isPipActive = false,
  isPipSupported = false,
  layoutMode = 'stage',
  onToggleLayout,
  recording = false,
  onToggleRecording,
  onLeave,
  className = '',
}) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full bg-gray-900/90 backdrop-blur-xl border border-white/15 shadow-2xl transition-all">
        {/* Microphone Toggle */}
        <button
          type="button"
          onClick={() => onToggleMic(!muted)}
          title={muted ? 'Включить микрофон' : 'Отключить микрофон'}
          className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-150 ${
            muted
              ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30'
              : 'bg-white/10 hover:bg-white/20 text-white border border-white/10 active:scale-95'
          }`}
        >
          {muted ? <MicOffIcon size={20} /> : <MicIcon size={20} />}
        </button>

        {/* Camera Toggle */}
        <button
          type="button"
          onClick={() => onToggleVideo(!videoOn)}
          title={videoOn ? 'Выключить камеру' : 'Включить камеру'}
          className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-150 ${
            !videoOn
              ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30'
              : 'bg-white/10 hover:bg-white/20 text-white border border-white/10 active:scale-95'
          }`}
        >
          {!videoOn ? <VideoOffIcon size={20} /> : <VideoOnIcon size={20} />}
        </button>

        {/* Flip Camera (Switch Front/Back) - Shown on mobile / multi-camera */}
        {hasMultipleCameras && onSwitchCamera && (
          <button
            type="button"
            onClick={onSwitchCamera}
            title="Переключить камеру (фронтальная / основная)"
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/10 flex items-center justify-center transition-all duration-150 active:scale-95"
          >
            <FlipCameraIcon size={20} />
          </button>
        )}

        {/* Picture-in-Picture Button (Native browser PiP) */}
        {isPipSupported && onTogglePip && (
          <button
            type="button"
            onClick={onTogglePip}
            title={isPipActive ? 'Вернуть из PiP' : 'Режим «Картинка в картинке»'}
            className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-150 ${
              isPipActive
                ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/40 ring-2 ring-primary-400'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10 active:scale-95'
            }`}
          >
            <PipIcon size={20} />
          </button>
        )}

        {/* Layout Toggle (Grid vs Stage/PiP) */}
        {onToggleLayout && (
          <button
            type="button"
            onClick={onToggleLayout}
            title={layoutMode === 'stage' ? 'Сетка участников' : 'Фокус на собеседнике'}
            className="hidden sm:flex w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/10 items-center justify-center transition-all duration-150 active:scale-95"
          >
            {layoutMode === 'stage' ? <GridIcon size={20} /> : <StageIcon size={20} />}
          </button>
        )}

        {/* Operator Recording Button */}
        {role === 'operator' && onToggleRecording && (
          <button
            type="button"
            onClick={onToggleRecording}
            title={recording ? 'Остановить запись' : 'Начать запись'}
            className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-150 ${
              recording
                ? 'bg-red-500/20 text-red-400 border border-red-500/50 animate-pulse'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10 active:scale-95'
            }`}
          >
            {recording ? <StopRecordIcon size={18} /> : <RecordIcon size={18} />}
          </button>
        )}

        {/* End / Leave Call Button */}
        <button
          type="button"
          onClick={onLeave}
          title="Завершить звонок"
          className="h-11 sm:h-12 px-4 sm:px-6 rounded-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-medium flex items-center gap-2 shadow-lg shadow-red-600/30 transition-all duration-150 active:scale-95"
        >
          <PhoneOffIcon size={20} />
          <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider">
            Выйти
          </span>
        </button>
      </div>
    </div>
  );
}